/**
 * academyLink.js — bidirectional Plan ↔ Academy matcher.
 *
 * Both directions share the same fuzzy keyword-overlap scorer so a
 * Plan lesson and an Academy skill can find each other without any
 * pre-computed join table.
 *
 * Direction A · Plan lesson → Academy skill
 *   `findAcademySkillFor({ subject, tier, title })`
 *   Loads all courses (once, cached), narrows to the matching
 *   subject + tier course, fetches its skills, scores each skill's
 *   name against the lesson title, and returns the best match if the
 *   overlap score is high enough (≥ 2 shared keyword-stems).
 *
 * Direction B · Academy skill → Plan week
 *   `findPlanWeeksForSkill(skill, plans)`
 *   Given the child's `plans` object and a skill, walks every day
 *   activity in every week and returns the week numbers where a
 *   lesson's title overlaps with the skill's name.
 *
 * All fetches are memoised on window so the two chips can render on
 * every card without hammering the API.
 */

const API = (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) || "";

const _cache = {
  courses: null,               // Promise<{courses:[...]}>
  courseBySlug: {},            // slug -> Promise<{course, units, skills}>
};

async function _fetchJSON(path) {
  const r = await fetch(`${API}${path}`, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}

async function _loadCourses() {
  if (!_cache.courses) _cache.courses = _fetchJSON("/api/academy/courses");
  const d = await _cache.courses;
  return Array.isArray(d) ? d : (d.courses || []);
}

async function _loadCourse(slug) {
  if (!_cache.courseBySlug[slug]) _cache.courseBySlug[slug] = _fetchJSON(`/api/academy/course/${slug}`);
  return _cache.courseBySlug[slug];
}

const STOPWORDS = new Set([
  "the","and","for","with","that","this","from","have","has","are","was","were","been","being","its","into","onto",
  "you","your","you're","yours","them","they","their","our","ours","some","more","most","less","few",
  "will","can","may","use","using","use.","step","learn","learning","lesson","topic","about","how","why","what","when","where",
  "of","in","on","by","to","a","an","is","it","or","as","at","be","up","do","go","yes","no",
]);

function _stems(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .split(/\s+/)
    .filter((w) => w && w.length >= 3 && !STOPWORDS.has(w))
    // very light suffix stripping so "counting" ≈ "count"
    .map((w) => w.replace(/(ing|ed|es|s)$/,""));
}

function _score(aStems, bStems) {
  if (!aStems.length || !bStems.length) return 0;
  const setB = new Set(bStems);
  let n = 0;
  for (const s of aStems) if (setB.has(s)) n++;
  return n;
}

/** Direction A — Plan lesson → best Academy skill (or null). */
export async function findAcademySkillFor({ subject, tier, title }) {
  if (!subject || !title) return null;
  try {
    const courses = await _loadCourses();
    // Course slug is "{tier}-{subject}" (e.g. "explorers-english"). If
    // we don't have a tier, fall back to the child's most-common tier
    // for that subject (explorers) since it's the widest audience.
    const t = tier || "explorers";
    const slug = `${t}-${subject}`;
    const target = courses.find((c) => c.slug === slug);
    if (!target) return null;
    const doc = await _loadCourse(slug);
    const skills = doc?.skills || [];
    if (!skills.length) return null;
    const aStems = _stems(title);
    let best = null; let bestScore = 0;
    for (const s of skills) {
      const bStems = _stems(`${s.name} ${s.description || ""}`);
      const sc = _score(aStems, bStems);
      if (sc > bestScore) { bestScore = sc; best = s; }
    }
    if (bestScore < 2) return null; // avoid weak matches
    return {
      skill: best,
      course: target,
      to: `/academy/${subject}/${slug}/${best.unit_slug}/${best.slug}`,
      score: bestScore,
    };
  } catch { return null; }
}

/** Direction B — Academy skill → weeks in the given plans. */
export function findPlanWeeksForSkill(skill, plans) {
  if (!skill || !plans) return [];
  const aStems = _stems(`${skill.name} ${skill.description || ""}`);
  const hits = []; // [{ childId, weekNumber, activityTitle }]
  for (const [childId, plan] of Object.entries(plans)) {
    const weeks = plan?.weeklyPlans || [];
    for (const wk of weeks) {
      const days = wk.days || [];
      const weekNum = wk.weekNumber || wk.week;
      let matchedTitle = null;
      for (const d of days) {
        for (const a of (d.activities || [])) {
          const title = a.outcomeShort || a.title || "";
          if (!title) continue;
          const bStems = _stems(title);
          if (_score(aStems, bStems) >= 2) { matchedTitle = title; break; }
        }
        if (matchedTitle) break;
      }
      if (matchedTitle && weekNum) hits.push({ childId, weekNumber: weekNum, activityTitle: matchedTitle });
    }
  }
  // Dedupe by (childId, weekNumber).
  const seen = new Set();
  return hits.filter((h) => {
    const k = `${h.childId}::${h.weekNumber}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
